/**
 * The telemetry singleton, and the error path that joins both sinks.
 *
 * One dispatcher per page. It is created here rather than in `index.ts` so
 * that the modules which need it — instrumentation, the console logger's
 * error sink — can depend on it without depending on the public barrel, and
 * so the import graph stays a tree.
 *
 * @module logging/telemetry
 */

import { setErrorSink } from './console-logger';
import { classifyError, describeError } from './core/classify';
import type { ErrorCategory } from './core/types';
import { reportError } from './error-reporting';
import { createDispatcher } from './pipeline/dispatcher';

/** The process-wide dispatcher. Emitting goes through this. */
export const telemetry = createDispatcher();

export interface LogErrorOptions {
  /** Override the keyword-derived bucket when the call site knows better. */
  readonly category?: ErrorCategory;
  /** Where it happened — a module or component name, not a file path. */
  readonly location?: string;
  /** The original thrown value, whose stack Error Reporting groups on. */
  readonly cause?: unknown;
}

/**
 * Record an error in both places at once.
 *
 * This is the function that makes "one error, two sinks" a single call
 * rather than a convention nobody follows: the dashboards get a countable
 * `web_error` event, and Cloud Error Reporting gets an `Error` with a stack
 * to group on. Forgetting one of the two was the most common way the old
 * split modules produced an error chart that disagreed with Error Reporting.
 *
 * Repeats of the same category and message inside a short window collapse
 * to one event, so a component erroring on every render cannot run up the
 * log-ingestion bill.
 */
export function logError(message: string, options: LogErrorOptions = {}): void {
  const text = options.location ? `${options.location}: ${message}` : message;
  const category = options.category ?? classifyError(text);

  telemetry.emit(
    'web_error',
    { error_category: category, message: text },
    { dedupeKey: `${category}:${text}` },
  );

  // Error Reporting wants a real stack. When the call site had one, use it;
  // otherwise synthesize an Error so the report still groups by call site
  // rather than being dropped.
  reportError(options.cause instanceof Error ? options.cause : new Error(text));
}

/**
 * Wire `createLogger(...).error()` into the pipeline.
 *
 * Runs on this module's load rather than inside an init function, so that
 * anything logging an error after `@logging` has been imported is reported
 * even if `initLogging()` has not been called yet.
 */
setErrorSink((namespace: string, message: string, cause: unknown) => {
  logError(message, {
    location: namespace,
    cause: cause instanceof Error ? cause : new Error(describeError(cause)),
  });
});
